/* Campaign hero: homepage. Vanilla custom element (Dawn style), replacing the
 * design handoff's DC-prototype component. See CLAUDE.md for the source spec.
 */

if (!customElements.get('campaign-hero')) {
  class CampaignHero extends HTMLElement {
    constructor() {
      super();
      this.view = 'landing';
      this.galleryIndex = 0;
      this.activeSlot = 0;
      this.videoFull = false;
      this.fragKeys = ['top', 'left', 'bl', 'br'];
      this.fragEls = {};
    }

    connectedCallback() {
      this.stage = this.querySelector('[data-stage]');
      this.field = this.querySelector('[data-field]');
      this.media = this.querySelector('[data-media]');
      this.video = this.querySelector('[data-video]');
      this.mediaToggle = this.querySelector('[data-media-toggle]');
      this.menuLayer = this.querySelector('[data-menu]');
      this.galleryLayer = this.querySelector('[data-gallery]');
      this.cartOpenBtn = this.querySelector('[data-cart-open]');
      this.cartCountLabel = this.querySelector('[data-cart-count-label]');
      this.gallerySlotA = this.querySelector('[data-gallery-slot="a"]');
      this.gallerySlotB = this.querySelector('[data-gallery-slot="b"]');
      this.galleryName = this.querySelector('[data-gallery-name]');
      this.galleryIndexLabel = this.querySelector('[data-gallery-index]');
      this.galleryLink = this.querySelector('[data-gallery-link]');
      this.fragKeys.forEach((key) => {
        this.fragEls[key] = this.querySelector('[data-frag="' + key + '"]');
      });

      // Rebuild (3rd attempt at MENU visibility on real phones - cfeca86's
      // svh fix and 48bf58b's isolation/z-index fix both proved insufficient
      // on an actual iPhone). Root cause this time isn't stacking at all:
      // .campaign-hero has overflow: hidden (needed to clip the oversized
      // drag field), and WebKit has a long-standing quirk where a
      // position:fixed descendant of an overflow:hidden ancestor can get
      // treated as clipped/contained by that ancestor instead of truly
      // fixed to the viewport - a spec violation Chrome doesn't reproduce,
      // which is exactly why this kept testing fine here and failing on the
      // owner's phone. Checked the task's own suspicion first: cart-drawer
      // never carries `active` at rest (snippets/cart-drawer.liquid only
      // ever renders class="drawer" / "drawer is-empty" server-side, and
      // cart-drawer.js only adds `active` inside open()), so the old
      // body:has(cart-drawer.active) rule wasn't misfiring - ruled out, not
      // the cause. The fix is structural: mount the button as a direct
      // child of body instead of a descendant of .campaign-hero, so no
      // ancestor's overflow, transform, filter or isolation can ever trap
      // it. See mountMenuButton() below.
      this.mountMenuButton();
      this.observeCartDrawer();

      this.items = this.readGalleryItems();
      this.reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.props = {
        pace: parseFloat(this.dataset.pace) || 1,
        monochrome: this.dataset.monochrome === 'true',
        parallaxStrength: parseFloat(this.dataset.parallaxStrength),
        repelRadius: parseFloat(this.dataset.repelRadius),
        repelStrength: parseFloat(this.dataset.repelStrength),
      };
      if (isNaN(this.props.parallaxStrength)) this.props.parallaxStrength = 6;
      if (isNaN(this.props.repelRadius)) this.props.repelRadius = 260;
      if (isNaN(this.props.repelStrength)) this.props.repelStrength = 46;

      this.set('--ch-stage', '1');
      this.set('--ch-stage-pe', 'auto');
      this.set('--ch-menu', '0');
      this.set('--ch-menu-pe', 'none');
      this.set('--ch-gal', '0');
      this.set('--ch-gal-pe', 'none');
      this.set('--ch-wm-out', '1');

      this.applyLayout();
      this.onResize = () => this.applyLayout();
      window.addEventListener('resize', this.onResize);

      this.onKey = (event) => this.handleKey(event);
      window.addEventListener('keydown', this.onKey);

      const galleryOpener = this.querySelector('[data-menu-gallery]');
      if (galleryOpener) galleryOpener.addEventListener('click', () => this.setView('gallery'));

      // Hardcoded "02 Film" menu entry, matching LandingPage.dc.html's menu
      // structure (01 Campaign 01, 02 skipped in the mock for exactly this -
      // owner has now locked it in, merchant items shift to 03+). Closes the
      // menu back to landing, then triggers video-full via the same
      // toggleVideoFull() the media-toggle button itself calls.
      const filmOpener = this.querySelector('[data-menu-film]');
      if (filmOpener) {
        filmOpener.addEventListener('click', () => {
          this.setView('landing');
          this.toggleVideoFull();
        });
      }

      const prevBtn = this.querySelector('[data-gallery-prev]');
      const nextBtn = this.querySelector('[data-gallery-next]');
      if (prevBtn) prevBtn.addEventListener('click', () => this.advanceGallery(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.advanceGallery(1));

      if (this.galleryLayer) {
        this.galleryLayer.addEventListener('touchstart', (event) => this.onGalleryTouchStart(event), { passive: true });
        this.galleryLayer.addEventListener('touchend', (event) => this.onGalleryTouchEnd(event));
      }

      if (this.mediaToggle) {
        this.mediaToggle.addEventListener('click', () => {
          // Bug fix: guards against the media-toggle button (nested inside
          // .campaign-hero__field) firing a synthetic click when a touch
          // drag/pan ends on top of it - same suppressClick guard as
          // LandingPage.dc.html's fieldPointerDown, needed now that touch can
          // start a drag here too (see onFieldPointerDown below).
          if (this.suppressClick) return;
          this.toggleVideoFull();
        });
      }

      this.setupCart();
      this.renderGalleryItem();
      this.setupField();
      this.setupVideo();
      this.armIntro();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('keydown', this.onKey);
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('dragstart', this.dragBlock);
      if (this.detachPan) this.detachPan();
      if (this.inertiaRaf) cancelAnimationFrame(this.inertiaRaf);
      clearTimeout(this.armTimer);
      clearTimeout(this.zoomTimer);
      clearTimeout(this.focusTimer);
      if (this.cartUnsubscribe) this.cartUnsubscribe();
      if (this.video && this.onVideoTime) this.video.removeEventListener('timeupdate', this.onVideoTime);
      if (this.cartDrawerObserver) this.cartDrawerObserver.disconnect();
      if (this.control && this.control.parentNode) this.control.parentNode.removeChild(this.control);
    }

    mountMenuButton() {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'campaign-hero__menu-btn';
      btn.setAttribute('aria-label', 'Open menu');
      const label = document.createElement('span');
      label.textContent = 'Menu';
      btn.appendChild(label);
      document.body.appendChild(btn);
      btn.addEventListener('click', () => this.setView(this.view === 'landing' ? 'menu' : 'landing'));
      this.control = btn;
      this.controlLabel = label;
    }

    observeCartDrawer() {
      const cartDrawerEl = document.querySelector('cart-drawer');
      if (!cartDrawerEl || !this.control) return;
      const syncState = () => {
        this.control.classList.toggle('campaign-hero__menu-btn--under-drawer', cartDrawerEl.classList.contains('active'));
      };
      syncState();
      this.cartDrawerObserver = new MutationObserver(syncState);
      this.cartDrawerObserver.observe(cartDrawerEl, { attributes: true, attributeFilter: ['class'] });
    }

    set(name, value) {
      this.style.setProperty(name, value);
      // The menu button is a direct child of body now (not a descendant of
      // <campaign-hero>), so it doesn't inherit these custom properties -
      // its opacity: calc(var(--ch-in) * var(--ch-chrome)) needs its own
      // copy to keep the same intro fade-in timing as the rest of the
      // hero's chrome (eyebrows, wordmark).
      if (this.control && (name === '--ch-in' || name === '--ch-chrome')) {
        this.control.style.setProperty(name, value);
      }
    }

    readGalleryItems() {
      const node = this.querySelector('[data-gallery-items]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent).filter((item) => item.image);
      } catch (error) {
        return [];
      }
    }

    /* ---- layout / responsive ---- */

    applyLayout() {
      const mobile = window.innerWidth < 750;
      this.mobile = mobile;
      const fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
      this.pointerOK = fine && !this.reducedMotion && !mobile;
      if (!this.pointerOK) {
        this.set('--ch-par-x', '0');
        this.set('--ch-par-y', '0');
        this.clearRepel();
      }
      const strength = this.props.parallaxStrength;
      this.set('--ch-par-per', strength + 'px');
      this.set('--ch-par-pri', (strength * 0.65).toFixed(1) + 'px');
      this.set('--ch-mono', this.props.monochrome ? 'grayscale(100%)' : 'grayscale(0%)');
      // Bug fix: these rem bounds are set here (not just the CSS fallback) so
      // JS always wins over the clamp() default in campaign-hero.css - same
      // 1rem=10px vs mock's 16px defect as everywhere else in this file,
      // rebased x1.6. Desktop value copied verbatim from LandingPage.dc.html;
      // the mobile value has no mock counterpart (theme-authored breakpoint)
      // but gets the same rebase for consistency.
      this.set('--ch-wm-fs', mobile ? 'clamp(9.6rem, 30vw, 16rem)' : 'clamp(14.4rem, 19vw, 36.8rem)');
      this.set('--ch-gal-w', mobile ? 'calc(100vw - 28px)' : 'min(91vw, 1600px)');
      this.set('--ch-gal-h', mobile ? 'min(76svh, 760px)' : 'min(65vh, 780px)');
      this.set('--ch-gal-cols', mobile ? '1fr' : '1fr 1fr');
      this.set('--ch-gal-rows', mobile ? '0.28fr 0.72fr' : 'none');
      this.computeVideoFrame();
    }

    computeVideoFrame() {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let restH = Math.min(vh * 0.86, 940);
      let restW = (restH * 9) / 16;
      if (restW > vw * 0.52) {
        restW = vw * 0.52;
        restH = (restW * 16) / 9;
      }
      this.restW = restW;
      this.restH = restH;
      this.coverScale = Math.max(vw / restW, vh / restH);
      this.set('--ch-vf-w', restW.toFixed(0) + 'px');
      this.set('--ch-vf-h', restH.toFixed(0) + 'px');
      this.set('--ch-media-z', this.videoFull ? '100' : '20');
      this.set('--ch-chrome', this.videoFull ? '0' : '1');
      if (!this.introRunning) {
        this.set('--ch-media-scale', this.videoFull ? this.coverScale.toFixed(3) : '1');
      }
    }

    /* ---- draggable / parallax field ---- */

    setupField() {
      if (!this.field) return;

      this.dragBlock = (event) => event.preventDefault();
      window.addEventListener('dragstart', this.dragBlock);

      this.onMouseMove = (event) => {
        if (!this.pointerOK) return;
        const nx = -((event.clientX / window.innerWidth) - 0.5) * 2;
        const ny = -((event.clientY / window.innerHeight) - 0.5) * 2;
        const mx = event.clientX;
        const my = event.clientY;
        if (this.parallaxRaf) return;
        this.parallaxRaf = requestAnimationFrame(() => {
          this.parallaxRaf = 0;
          this.set('--ch-par-x', nx.toFixed(3));
          this.set('--ch-par-y', ny.toFixed(3));
          this.applyRepel(mx, my);
        });
      };
      window.addEventListener('mousemove', this.onMouseMove);

      this.field.addEventListener('pointerdown', (event) => this.onFieldPointerDown(event));
    }

    applyRepel(mx, my) {
      const radius = this.props.repelRadius;
      const strength = this.props.repelStrength;
      this.fragKeys.forEach((key) => {
        const el = this.fragEls[key];
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = cx - mx;
        const dy = cy - my;
        const dist = Math.hypot(dx, dy) || 1;
        if (strength > 0 && dist < radius) {
          const mag = (1 - dist / radius) * strength;
          this.set('--ch-rep-' + key + '-x', ((dx / dist) * mag).toFixed(1) + 'px');
          this.set('--ch-rep-' + key + '-y', ((dy / dist) * mag).toFixed(1) + 'px');
        } else {
          this.set('--ch-rep-' + key + '-x', '0px');
          this.set('--ch-rep-' + key + '-y', '0px');
        }
      });
    }

    clearRepel() {
      this.fragKeys.forEach((key) => {
        this.set('--ch-rep-' + key + '-x', '0px');
        this.set('--ch-rep-' + key + '-y', '0px');
      });
    }

    onFieldPointerDown(event) {
      // Bug fix: this used to hard-block event.pointerType === 'touch' here
      // (Phase 2's pointer:fine-only gate). LandingPage.dc.html's own
      // fieldPointerDown has no such exclusion - it's plain Pointer Events
      // and works the same for mouse and touch there. The 1-of-1 standard
      // means touch gets the same drag/pan as desktop; reduced-motion is the
      // only thing that still opts touch out (matches the constraint that
      // this is a deliberate user-driven interaction, not autoplaying
      // motion, but on touch a swipe can *also* be a scroll gesture, so
      // skip starting our own drag entirely when the user has asked for
      // less motion rather than fighting the browser over which gesture
      // wins). .campaign-hero__field's touch-action: pan-y (unchanged)
      // still lets the browser take a vertical swipe as native scroll and
      // deliver us a pointercancel instead of pointermove - handled below,
      // same as before.
      if (event.pointerType === 'touch' && this.reducedMotion) return;
      if (this.view !== 'landing' || this.videoFull) return;
      if (event.button !== undefined && event.button !== 0) return;
      this.cancelInertia();

      const startX = event.clientX;
      const startY = event.clientY;
      const baseX = this.panX || 0;
      const baseY = this.panY || 0;
      const MAX_X = 180;
      const MAX_Y = 120;
      let dragging = false;
      let lastX = startX;
      let lastY = startY;
      let lastT = performance.now();
      let vx = 0;
      let vy = 0;

      const move = (moveEvent) => {
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;
        if (!dragging && Math.hypot(dx, dy) > 7) {
          dragging = true;
          this.suppressClick = true;
          this.set('--ch-drag-cursor', 'grabbing');
        }
        if (!dragging) return;
        const now = performance.now();
        const dt = Math.max(1, now - lastT);
        vx = (moveEvent.clientX - lastX) / dt;
        vy = (moveEvent.clientY - lastY) / dt;
        lastX = moveEvent.clientX;
        lastY = moveEvent.clientY;
        lastT = now;
        this.panX = Math.max(-MAX_X, Math.min(MAX_X, baseX + dx));
        this.panY = Math.max(-MAX_Y, Math.min(MAX_Y, baseY + dy));
        this.set('--ch-pan-x', this.panX.toFixed(1) + 'px');
        this.set('--ch-pan-y', this.panY.toFixed(1) + 'px');
      };

      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        this.detachPan = null;
        this.set('--ch-drag-cursor', 'grab');
        if (!dragging) return;
        // Bug fix: matches LandingPage.dc.html's fieldPointerDown - clears
        // suppressClick on the next tick so the media-toggle button ignores
        // the synthetic click a touch drag-release can fire on it, but
        // still handles genuine taps (which never set dragging/suppressClick
        // in the first place, since dragging only flips true past the 7px
        // movement threshold above).
        setTimeout(() => { this.suppressClick = false; }, 0);
        if (!this.reducedMotion && (Math.abs(vx) > 0.02 || Math.abs(vy) > 0.02)) {
          this.startInertia(vx, vy, MAX_X, MAX_Y);
        }
      };

      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
      this.detachPan = up;
    }

    startInertia(vx, vy, maxX, maxY) {
      let velX = vx * 14;
      let velY = vy * 14;
      const step = () => {
        velX *= 0.92;
        velY *= 0.92;
        this.panX = Math.max(-maxX, Math.min(maxX, (this.panX || 0) + velX));
        this.panY = Math.max(-maxY, Math.min(maxY, (this.panY || 0) + velY));
        this.set('--ch-pan-x', this.panX.toFixed(1) + 'px');
        this.set('--ch-pan-y', this.panY.toFixed(1) + 'px');
        if (Math.abs(velX) > 0.06 || Math.abs(velY) > 0.06) {
          this.inertiaRaf = requestAnimationFrame(step);
        } else {
          this.inertiaRaf = 0;
        }
      };
      this.inertiaRaf = requestAnimationFrame(step);
    }

    cancelInertia() {
      if (this.inertiaRaf) {
        cancelAnimationFrame(this.inertiaRaf);
        this.inertiaRaf = 0;
      }
    }

    resetPan() {
      this.panX = 0;
      this.panY = 0;
      this.cancelInertia();
      this.set('--ch-pan-x', '0px');
      this.set('--ch-pan-y', '0px');
    }

    /* ---- video: zoom choreography ---- */

    setupVideo() {
      if (!this.video) return;
      this.video.muted = true;
      this.video.loop = true;
      this.video.playsInline = true;
      this.video.setAttribute('playsinline', '');
      this.video.setAttribute('muted', '');
      this.video.autoplay = true;
      const playPromise = this.video.play();
      if (playPromise && playPromise.catch) playPromise.catch(() => {});
    }

    armIntro() {
      const arm = () => {
        void this.offsetWidth;
        this.set('--ch-in', '1');
        this.set('--ch-drift-state', 'running');
      };
      requestAnimationFrame(() => requestAnimationFrame(arm));
      this.armTimer = setTimeout(arm, 150);

      if (this.reducedMotion) {
        this.set('--ch-media-scale-dur', Math.round(1200 * this.props.pace) + 'ms');
        this.set('--ch-media-scale', '1');
        this.set('--ch-wm-top', '40%');
        this.set('--ch-media-op', '1');
        return;
      }

      this.introRunning = true;
      this.computeVideoFrame();
      this.set('--ch-media-scale-dur', '0ms');
      this.set('--ch-media-scale', (this.coverScale || 1).toFixed(3));
      this.set('--ch-wm-top', '50%');
      this.set('--ch-media-op', '0');
      requestAnimationFrame(() => requestAnimationFrame(() => this.set('--ch-media-op', '1')));

      const ZOOM_CUE = 2.71;
      this.zoomedOut = false;
      const triggerZoomOut = () => {
        if (this.zoomedOut) return;
        this.zoomedOut = true;
        this.set('--ch-media-scale-dur', Math.round(1200 * this.props.pace) + 'ms');
        void this.offsetWidth;
        this.set('--ch-media-scale', '1');
        this.set('--ch-wm-top', '40%');
        this.introRunning = false;
        if (this.video && this.onVideoTime) this.video.removeEventListener('timeupdate', this.onVideoTime);
      };
      this.onVideoTime = () => {
        if (this.video && this.video.currentTime >= ZOOM_CUE) triggerZoomOut();
      };
      if (this.video) this.video.addEventListener('timeupdate', this.onVideoTime);
      this.zoomTimer = setTimeout(triggerZoomOut, Math.round(6000 * this.props.pace));
    }

    toggleVideoFull() {
      const next = !this.videoFull;
      if (next) this.resetPan();
      this.set('--ch-wm-top', next ? '50%' : '40%');
      this.videoFull = next;
      if (this.mediaToggle) {
        this.mediaToggle.setAttribute('aria-expanded', next ? 'true' : 'false');
        this.mediaToggle.setAttribute(
          'aria-label',
          next ? 'Shrink campaign film' : 'Expand campaign film'
        );
      }
      this.computeVideoFrame();
    }

    /* ---- view state: landing / menu / gallery ---- */

    handleKey(event) {
      if (event.key === 'Escape') {
        if (this.view !== 'landing') {
          this.setView('landing');
          return;
        }
        if (this.videoFull) {
          this.toggleVideoFull();
          return;
        }
      }
      if (this.view === 'gallery') {
        if (event.key === 'ArrowRight') this.advanceGallery(1);
        if (event.key === 'ArrowLeft') this.advanceGallery(-1);
      }
      if (event.key === 'Tab' && this.view !== 'landing') this.trapFocus(event);
    }

    setView(next) {
      this.set('--ch-wm-out', next === 'gallery' ? '0' : '1');
      if (next === 'gallery' && this.view === 'landing') {
        clearTimeout(this.viewTimer);
        this.viewTimer = setTimeout(() => this.applyView(next), Math.round(420 * this.props.pace));
      } else {
        this.applyView(next);
      }
    }

    applyView(next) {
      if (next !== 'landing') {
        this.resetPan();
        if (this.videoFull) this.toggleVideoFull();
      }
      const menu = next === 'menu';
      const gallery = next === 'gallery';
      this.set('--ch-menu', menu ? '1' : '0');
      this.set('--ch-menu-pe', menu ? 'auto' : 'none');
      this.set('--ch-gal', gallery ? '1' : '0');
      this.set('--ch-gal-pe', gallery ? 'auto' : 'none');
      this.set('--ch-stage', next === 'landing' ? '1' : (menu ? '0.06' : '0'));
      this.set('--ch-stage-pe', next === 'landing' ? 'auto' : 'none');
      this.set('--ch-drift-state', next === 'landing' ? 'running' : 'paused');
      if (this.video) {
        if (next === 'landing') {
          const playPromise = this.video.play();
          if (playPromise && playPromise.catch) playPromise.catch(() => {});
        } else {
          this.video.pause();
        }
      }
      this.menuLayer.setAttribute('aria-hidden', menu ? 'false' : 'true');
      this.galleryLayer.setAttribute('aria-hidden', gallery ? 'false' : 'true');
      this.view = next;
      if (this.controlLabel) this.controlLabel.textContent = next === 'landing' ? 'Menu' : 'Close';
      if (this.control) {
        this.control.setAttribute('aria-label', next === 'landing' ? 'Open menu' : (next === 'menu' ? 'Close menu' : 'Close gallery'));
      }
      clearTimeout(this.focusTimer);
      this.focusTimer = setTimeout(() => {
        if (menu) {
          const first = this.menuLayer.querySelector('a, button');
          if (first) first.focus();
        } else if (this.control) {
          this.control.focus();
        }
      }, 620);
    }

    trapFocus(event) {
      const layer = this.view === 'menu' ? this.menuLayer : this.galleryLayer;
      if (!layer || !this.control) return;
      const focusable = Array.prototype.slice.call(layer.querySelectorAll('a, button')).concat(this.control);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---- gallery ---- */

    renderGalleryItem() {
      if (!this.items.length) return;
      const item = this.items[this.galleryIndex];
      const activeEl = this.activeSlot === 0 ? this.gallerySlotA : this.gallerySlotB;
      if (activeEl) {
        activeEl.src = item.image;
        activeEl.alt = item.title || '';
        activeEl.setAttribute('data-active', 'true');
      }
      const inactiveEl = this.activeSlot === 0 ? this.gallerySlotB : this.gallerySlotA;
      if (inactiveEl) inactiveEl.removeAttribute('data-active');
      if (this.galleryName) this.galleryName.textContent = item.title || '';
      if (this.galleryIndexLabel) {
        const n = this.items.length;
        this.galleryIndexLabel.textContent =
          String(this.galleryIndex + 1).padStart(2, '0') + ' / ' + String(n).padStart(2, '0');
      }
      if (this.galleryLink) {
        if (item.link) {
          this.galleryLink.href = item.link;
          this.galleryLink.removeAttribute('aria-disabled');
        } else {
          this.galleryLink.href = '#';
          this.galleryLink.setAttribute('aria-disabled', 'true');
        }
      }
    }

    advanceGallery(direction) {
      if (!this.items.length) return;
      const n = this.items.length;
      this.galleryIndex = (this.galleryIndex + direction + n) % n;
      this.activeSlot = this.activeSlot === 0 ? 1 : 0;
      this.renderGalleryItem();
    }

    onGalleryTouchStart(event) {
      this.touchX = event.touches && event.touches[0] ? event.touches[0].clientX : null;
    }

    onGalleryTouchEnd(event) {
      if (this.touchX === null || this.touchX === undefined) return;
      const changed = event.changedTouches && event.changedTouches[0];
      const x = changed ? changed.clientX : this.touchX;
      const delta = x - this.touchX;
      this.touchX = null;
      if (Math.abs(delta) > 40) this.advanceGallery(delta < 0 ? 1 : -1);
    }

    /* ---- cart: real cart.item_count, real Dawn cart-drawer ---- */

    setupCart() {
      this.updateCartCount(parseInt(this.dataset.cartCount, 10) || 0);
      if (this.cartOpenBtn) {
        this.cartOpenBtn.addEventListener('click', () => {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open(this.cartOpenBtn);
        });
      }
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.cartData && typeof event.cartData.item_count === 'number') {
            this.updateCartCount(event.cartData.item_count);
          }
        });
      }
    }

    updateCartCount(count) {
      if (!this.cartCountLabel) return;
      this.cartCountLabel.textContent = count > 0 ? ' ' + count : '';
    }
  }

  customElements.define('campaign-hero', CampaignHero);
}
